import React, { useCallback, useEffect, useState } from 'react';
import { confirmAlert } from 'react-confirm-alert';
import { useHistory } from 'react-router-dom';
import { toast } from 'react-toastify';
import { apiConfigs } from '../../../api/APIConfigs';
import { Church } from '../../../api/models/Church';
import { routes } from '../../../route-list';
import { useAsyncRequest } from '../../../util/use-async-request';
import { useRequest } from '../../../util/use-request';
import { RhConfirmModal } from '../../core/RhConfirmModal';
import { ChurchScreen } from '../components/ChurchScreen';

export type ChurchProps = {
  // Outputs
  churches: Church[];
  isLoading: boolean;
  // Inputs
  /**
   * Called when the user clicks the "Add Section" button
   * to indicate that they want to add a new section to the mission statement
   **/
  userClickedAddSection: () => void;
  userClickedEditSection: (id: string) => void;
  userClickedDeleteSection: (id: string) => void;
};

/**
 * Why is this not following the same structure as (link below)?
 * https://github.com/smashingboxes/arno/blob/dev/src/modules/home/containers/HomeContainer.tsx
 *
 * Reason 1: Don’t call Hooks inside loops, conditions, or nested functions
 * (https://reactjs.org/docs/hooks-rules.html#only-call-hooks-at-the-top-level)
 * Reason 2: Don’t call Hooks from regular JavaScript functions.
 * (https://reactjs.org/docs/hooks-rules.html#only-call-hooks-from-react-functions)
 */
export const ChurchContainer = () => {
  const history = useHistory();
  const request = useRequest();

  const [churches, setChurches] = useState<Church[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  const loadChurches = useCallback(async () => {
    try {
      setIsLoading(true);
      const result = await request<Church[]>(apiConfigs.getChurches());
      setChurches(result);
    } catch (e) {
      toast.error('Failed to loading churches!');
    } finally {
      setIsLoading(false);
    }
  }, [request]);

  useEffect(() => {
    loadChurches();
  }, [loadChurches]);

  return (
    <ChurchScreen
      churches={churches || []}
      isLoading={isLoading}
      userClickedAddSection={() => {
        history.push(routes.CHURCH__ADD);
      }}
      userClickedEditSection={id => {
        history.push(routes.CHURCH__EDIT(id));
      }}
      userClickedDeleteSection={id => {
        confirmAlert({
          customUI: options => (
            <RhConfirmModal
              isOpen={true}
              description="Are you sure you want to delete this section?"
              onClose={options.onClose}
              onOk={async () => {
                try {
                  await request(apiConfigs.deleteChurch(id));
                  toast.success('Section deleted!');
                  loadChurches();
                  options.onClose();
                } catch (e: any) {
                  toast.error(e.message || 'Failed to delete section!');
                }
              }}
            />
          ),
        });
      }}
    />
  );
};
